import { ErrorMessage } from '@occc-app/lib/ErrorMessage';

/* Action Types */

const EARNING_MECHANISM_COMPONENT_ERR = 'hq/Offer/Constraints/EARNING_MECHANISM_COMPONENT_ERR';
const EARNING_MECHANISM_COMPONENT_OK = 'hq/Offer/Constraints/EARNING_MECHANISM_COMPONENT_OK';
const REQUEST_EARNING_MECHANISM = 'hq/Offer/EarningMechanisms/REQUEST_EARNING_MECHANISM';
const REQUEST_EARNING_MECHANISM_ERR = 'hq/Offer/EarningMechanisms/REQUEST_EARNING_MECHANISM_ERR';
const REQUEST_EARNING_MECHANISM_OK = 'hq/Offer/EarningMechanisms/REQUEST_EARNING_MECHANISM_OK';
const REQUEST_EARNING_MECHANISMS_OK = 'hq/Offer/EarningMechanisms/REQUEST_EARNING_MECHANISMS_OK';
const RESET_INITIAL_EARNING_MECHANISM_STATE =
  'hq/Offer/EarningMechanisms/RESET_INITIAL_EARNING_MECHANISM_STATE';

type Types = {
  EARNING_MECHANISM_COMPONENT_ERR: typeof EARNING_MECHANISM_COMPONENT_ERR;
  EARNING_MECHANISM_COMPONENT_OK: typeof EARNING_MECHANISM_COMPONENT_OK;
  REQUEST_EARNING_MECHANISM: typeof REQUEST_EARNING_MECHANISM;
  REQUEST_EARNING_MECHANISM_ERR: typeof REQUEST_EARNING_MECHANISM_ERR;
  REQUEST_EARNING_MECHANISM_OK: typeof REQUEST_EARNING_MECHANISM_OK;
  REQUEST_EARNING_MECHANISMS_OK: typeof REQUEST_EARNING_MECHANISMS_OK;
  RESET_INITIAL_EARNING_MECHANISM_STATE: typeof RESET_INITIAL_EARNING_MECHANISM_STATE;
};

const types: Types = {
  EARNING_MECHANISM_COMPONENT_ERR,
  EARNING_MECHANISM_COMPONENT_OK,
  REQUEST_EARNING_MECHANISM,
  REQUEST_EARNING_MECHANISM_ERR,
  REQUEST_EARNING_MECHANISM_OK,
  REQUEST_EARNING_MECHANISMS_OK,
  RESET_INITIAL_EARNING_MECHANISM_STATE,
};

// Configurations
type CountProductsBy = 'quantity' | 'money_amount';
type ItemKeys = 'group' | 'upc' | 'sku';
type RoundCountedProducts = 'half_up' | 'down';
type RoundMoneyAmount = 'raw' | 'half_up' | 'down';
type SummationModes = 'include' | 'exclude';

type ItemDefinition = {
  countPer: CountProductsBy;
  itemKey: ItemKeys;
  summationMode: SummationModes;
  values: string[];
};

export const EXTERNAL_FULFILLMENT = 'externalFulfillment';
type ExternalFulfillment = {
  type: typeof EXTERNAL_FULFILLMENT;
};

export const LOYALTY_EVENT = 'loyaltyEvent';
type LoyaltyEvent = {
  type: typeof LOYALTY_EVENT;
  eventType: string;
};

export const MEMBERSHIP_EVENT = 'membershipEvent';
type MembershipEvent = {
  type: typeof MEMBERSHIP_EVENT;
  eventType: 'created' | 'active' | 'birthday';
};

export const PER_GROUP_PURCHASED = 'perGroupPurchased';
export type PerGroupPurchasedGroup = ItemDefinition & {
  countThreshold: number;
  dirty: boolean;
  groupId?: string;
  groupIndex: number;
  removed: boolean;
};
export type PerGroupPurchased = {
  type: typeof PER_GROUP_PURCHASED;
  groups: PerGroupPurchasedGroup[];
};

export const PER_ITEM_PURCHASED = 'perItemPurchased';
type PerItemPurchased = ItemDefinition & {
  type: typeof PER_ITEM_PURCHASED;
  countRounding: RoundCountedProducts;
};

export const PER_ITEMS_ACCUMULATED = 'perItemsAccumulated';
type PerItemsAccumulated = ItemDefinition & {
  type: typeof PER_ITEMS_ACCUMULATED;
  countThreshold: number;
};

export const PER_TAGGED_GROUP_PURCHASED = 'perTaggedGroupPurchased';
export type PerTaggedGroupPurchasedGroup = {
  dirty: boolean;
  groupId?: string;
  groupIndex: number;
  countPer: CountProductsBy;
  countThreshold: number;
  removed: boolean;
  summationMode: SummationModes;
  tags: string[];
};
export type PerTaggedGroupPurchased = {
  type: typeof PER_TAGGED_GROUP_PURCHASED;
  groups: PerTaggedGroupPurchasedGroup[];
};

export const PER_TAGGED_ITEM_PURCHASED = 'perTaggedItemPurchased';
type PerTaggedItemPurchased = {
  type: typeof PER_TAGGED_ITEM_PURCHASED;
  countPer: CountProductsBy;
  countRounding: RoundCountedProducts;
  summationMode: SummationModes;
  tags: string[];
};

export const PER_TAGGED_ITEMS_ACCUMULATED = 'perTaggedItemsAccumulated';
type PerTaggedItemsAccumulated = {
  type: typeof PER_TAGGED_ITEMS_ACCUMULATED;
  countPer: CountProductsBy;
  countThreshold: number;
  summationMode: SummationModes;
  tags: string[];
};

export const PRODUCT_PURCHASE = 'productPurchase';
type ProductPurchase = {
  type: typeof PRODUCT_PURCHASE;
  countProductsBy: CountProductsBy;
  eligibleProductGroups: string[];
  purchaseThreshold: number;
};

export const TOTAL_ELIGIBLE_PURCHASE = 'totalEligiblePurchase';
type TotalEligiblePurchase = {
  type: typeof TOTAL_ELIGIBLE_PURCHASE;
  countProductsBy: CountProductsBy;
  eligibleProductGroups: string[];
  roundCountedProducts: RoundCountedProducts;
};

export const TOTAL_ELIGIBLE_PURCHASE_OVER_TIME = 'totalEligiblePurchaseOverTime';
type TotalEligiblePurchaseOverTime = {
  type: typeof TOTAL_ELIGIBLE_PURCHASE_OVER_TIME;
  accumulationThreshold: number;
  countProductsBy: CountProductsBy;
  eligibleProductGroups: string[];
};

export const TOTAL_SPEND_ON_TRANSACTION = 'totalSpendOnTransaction';
type TotalSpendOnTransaction = {
  type: typeof TOTAL_SPEND_ON_TRANSACTION;
  ineligibleProductGroups: string[];
  roundMoneyAmount: RoundMoneyAmount;
};

export const TOTAL_SPEND_OVER_TIME = 'totalSpendOverTime';
type TotalSpendOverTime = {
  type: typeof TOTAL_SPEND_OVER_TIME;
  accumulationThreshold: number;
  ineligibleProductGroups: string[];
};

export type Product = {
  countProductsBy: CountProductsBy;
  eligibleProductGroups: string[];
  productId?: string;
  productIndex: number;
  purchaseThreshold: number;
  dirty: boolean;
  removed: boolean;
};

export const VARIETY_PURCHASE = 'varietyPurchase';
export type VarietyPurchase = {
  type: typeof VARIETY_PURCHASE;
  products: Product[];
};

export const VARIETY_PURCHASE_OVER_TIME = 'varietyPurchaseOverTime';
export type VarietyPurchaseOverTime = {
  type: typeof VARIETY_PURCHASE_OVER_TIME;
  products: Product[];
};

export const VISITS = 'visits';
type Visits = {
  type: typeof VISITS;
  visitThreshold: number;
};

export type EarningMechanismKeys =
  | typeof EXTERNAL_FULFILLMENT
  | typeof LOYALTY_EVENT
  | typeof MEMBERSHIP_EVENT
  | typeof PER_GROUP_PURCHASED
  | typeof PER_ITEM_PURCHASED
  | typeof PER_ITEMS_ACCUMULATED
  | typeof PER_TAGGED_GROUP_PURCHASED
  | typeof PER_TAGGED_ITEM_PURCHASED
  | typeof PER_TAGGED_ITEMS_ACCUMULATED
  | typeof PRODUCT_PURCHASE
  | typeof TOTAL_ELIGIBLE_PURCHASE
  | typeof TOTAL_ELIGIBLE_PURCHASE_OVER_TIME
  | typeof TOTAL_SPEND_ON_TRANSACTION
  | typeof TOTAL_SPEND_OVER_TIME
  | typeof VARIETY_PURCHASE
  | typeof VARIETY_PURCHASE_OVER_TIME
  | typeof VISITS;

export type EarningMechanisms =
  | TotalEligiblePurchaseOverTime
  | TotalEligiblePurchase
  | ExternalFulfillment
  | LoyaltyEvent
  | MembershipEvent
  | PerGroupPurchased
  | PerItemPurchased
  | PerItemsAccumulated
  | PerTaggedGroupPurchased
  | PerTaggedItemPurchased
  | PerTaggedItemsAccumulated
  | ProductPurchase
  | TotalSpendOverTime
  | TotalSpendOnTransaction
  | VarietyPurchase
  | VarietyPurchaseOverTime
  | Visits;

// Awards
const EXTERNAL_POINT_AWARD = 'externalPointAward';
type ExternalPointAward = {
  type: typeof EXTERNAL_POINT_AWARD;
  maximum: number;
};

const POINT_AWARD = 'pointAward';
type PointAward = {
  type: typeof POINT_AWARD;
  points: number;
};

const POINTS_PER_AWARD = 'pointsPerAward';
type PointsPerAward = {
  type: typeof POINTS_PER_AWARD;
  points: number;
  pointRounding: 'half_up' | 'down';
};

const NULL_AWARD = 'nullAward';
type NullAward = {
  type: typeof NULL_AWARD;
};

export type AwardKeys =
  | typeof EXTERNAL_POINT_AWARD
  | typeof POINT_AWARD
  | typeof POINTS_PER_AWARD
  | typeof NULL_AWARD;

export type Awards = ExternalPointAward | PointAward | PointsPerAward | NullAward;
/* Actions */

type EarningMechanismComponentERR = (
  errorMessages: ErrorMessage[],
  error: Error,
) => {
  type: typeof EARNING_MECHANISM_COMPONENT_ERR;
  errorMessages: ErrorMessage[];
  error: Error;
};

type EarningMechanismComponentOK = (id: string) => {
  type: typeof EARNING_MECHANISM_COMPONENT_OK;
  id: string;
};

type RequestEarningMechanism = (id: string) => {
  type: typeof REQUEST_EARNING_MECHANISM;
  id: string;
};

type RequestEarningMechanismERR = (
  errorMessages: ErrorMessage[],
  error: Error,
) => {
  type: typeof REQUEST_EARNING_MECHANISM_ERR;
  errorMessages: ErrorMessage[];
  error: Error;
};

type RequestEarningMechanismOK = (
  selectedAwardConfiguration: Awards,
  selectedEMConfiguration: EarningMechanisms,
) => {
  type: typeof REQUEST_EARNING_MECHANISM_OK;
  selectedAwardConfiguration: Awards;
  selectedEMConfiguration: EarningMechanisms;
};

type RequestEarningMechanismsOK = () => {
  type: typeof REQUEST_EARNING_MECHANISMS_OK;
};

type ResetInitialEarninMechanismState = (earningMechanisms: State) => {
  type: typeof RESET_INITIAL_EARNING_MECHANISM_STATE;
  earningMechanisms: State;
};

export type Action =
  | ReturnType<EarningMechanismComponentERR>
  | ReturnType<EarningMechanismComponentOK>
  | ReturnType<RequestEarningMechanism>
  | ReturnType<RequestEarningMechanismERR>
  | ReturnType<RequestEarningMechanismOK>
  | ReturnType<RequestEarningMechanismsOK>
  | ReturnType<ResetInitialEarninMechanismState>;

export type ActionCreators = {
  EarningMechanismComponentERR: EarningMechanismComponentERR;
  EarningMechanismComponentOK: EarningMechanismComponentOK;
  RequestEarningMechanism: RequestEarningMechanism;
  RequestEarningMechanismERR: RequestEarningMechanismERR;
  RequestEarningMechanismOK: RequestEarningMechanismOK;
  RequestEarningMechanismsOK: RequestEarningMechanismsOK;
  ResetInitialEarninMechanismState: ResetInitialEarninMechanismState;
};

/* State Types */

type OfferComponentMetaData = {
  error: Error | null;
  errorMessages: ErrorMessage[];
  id: string;
  dirty: boolean;
  requesting: boolean;
  submitting: boolean;
};

export type State = OfferComponentMetaData & {
  selectedAward: AwardKeys | null;
  selectedEarningMechanism: EarningMechanismKeys | null;
  emConfiguration: {
    [EXTERNAL_FULFILLMENT]: ExternalFulfillment;
    [LOYALTY_EVENT]: LoyaltyEvent;
    [MEMBERSHIP_EVENT]: MembershipEvent;
    [PER_GROUP_PURCHASED]: PerGroupPurchased;
    [PER_ITEM_PURCHASED]: PerItemPurchased;
    [PER_ITEMS_ACCUMULATED]: PerItemsAccumulated;
    [PER_TAGGED_GROUP_PURCHASED]: PerTaggedGroupPurchased;
    [PER_TAGGED_ITEM_PURCHASED]: PerTaggedItemPurchased;
    [PER_TAGGED_ITEMS_ACCUMULATED]: PerTaggedItemsAccumulated;
    [PRODUCT_PURCHASE]: ProductPurchase;
    [TOTAL_ELIGIBLE_PURCHASE]: TotalEligiblePurchase;
    [TOTAL_ELIGIBLE_PURCHASE_OVER_TIME]: TotalEligiblePurchaseOverTime;
    [TOTAL_SPEND_ON_TRANSACTION]: TotalSpendOnTransaction;
    [TOTAL_SPEND_OVER_TIME]: TotalSpendOverTime;
    [VARIETY_PURCHASE]: VarietyPurchase;
    [VARIETY_PURCHASE_OVER_TIME]: VarietyPurchaseOverTime;
    [VISITS]: Visits;
  };
  awardConfiguration: {
    [EXTERNAL_POINT_AWARD]: ExternalPointAward;
    [POINT_AWARD]: PointAward;
    [POINTS_PER_AWARD]: PointsPerAward;
    [NULL_AWARD]: NullAward;
  };
};

export default types;
